import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ApplicationInsightsService } from '@services/application-insights.service';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { BehaviorSubject, catchError, filter, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { UserInfo } from '@models/user-info.model';
import { MockUser } from '@environments/environment.types';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly redirectUrlStorageKey: string = 'LIB_REDIRECT_URL';
  userRoles: string[] = [];
  userEmail!: string;
  private mockUserName: string = '';
  private isMockMode: boolean = false;
  private mockToken: string = '';
  // Synchronous BehaviorSubject so that consumers (e.g. AppComponent dispatching
  // setUserInfo) react within the same call stack as setMockUser/OIDC emission.
  // A signal + toObservable emits asynchronously, which let route guards run
  // before userInfo was populated and denied access (PBI: rxjs→signal regression).
  private readonly authenticated$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly router: Router,
    private readonly loggingService: ApplicationInsightsService,
  ) {
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((auth) => this.authenticated$.next(auth.isAuthenticated)),
        filter((auth) => auth.isAuthenticated),
        switchMap(() => this.oidcSecurityService.getPayloadFromAccessToken(false)),
        filter(Boolean),
      )
      .subscribe((payload) => {
        if (!payload?.role) {
          this.userRoles = [];
        } else if (Array.isArray(payload.role)) {
          this.userRoles = payload.role;
        } else if (typeof payload.role === 'string') {
          this.userRoles = [payload.role];
        } else {
          this.userRoles = [];
        }

        if (payload.email) {
          this.userEmail = payload.email;
        }
      });
  }

  /**
   * Set mock user data for testing/development (bypasses OIDC authentication)
   */
  public setMockUser(mockUser: MockUser): void {
    this.isMockMode = true;
    this.userEmail = mockUser.email;
    this.mockUserName = mockUser.name;
    this.userRoles = mockUser.roles || [];

    // Create a mock JWT token
    this.mockToken = this.createMockToken(mockUser);

    // Emit authenticated state
    this.authenticated$.next(true);
  }

  /**
   * Creates a mock JWT token for development/testing
   */
  private createMockToken(mockUser: MockUser): string {
    const header = { alg: 'none', typ: 'JWT' };
    const now = Math.floor(Date.now() / 1000);
    const payload = {
      sub: 'mock-user-id',
      name: mockUser.name,
      email: mockUser.email,
      role: mockUser.roles,
      iat: now,
      exp: now + 86400, // 24 hours
    };

    const base64Header = btoa(JSON.stringify(header));
    const base64Payload = btoa(JSON.stringify(payload));

    return `${base64Header}.${base64Payload}.mock-signature`;
  }
  public authorize(url: string): void {
    sessionStorage.setItem(this.redirectUrlStorageKey, url);
    this.oidcSecurityService.authorize();
  }

  public handleCallback() {
    this.checkAuth()
      .pipe(
        catchError((error) => {
          this.loggingService.trackException(error);
          this.router.navigate(['/']);
          return throwError(() => 'Probleem met controleren van authenticatie');
        }),
        tap(() => {
          const redirectUrl = sessionStorage.getItem(this.redirectUrlStorageKey);
          sessionStorage.removeItem(this.redirectUrlStorageKey);
          this.router.navigateByUrl(redirectUrl || '/');
        }),
      )
      .subscribe();
  }

  public logoff() {
    this.oidcSecurityService.logoff().subscribe();
  }

  public checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth().pipe(takeUntilDestroyed(this.destroyRef));
  }

  public isAuthenticated(): Observable<boolean> {
    return this.authenticated$;
  }

  public getAccessToken(): Observable<string> {
    if (this.isMockMode) {
      return of(this.mockToken);
    }
    return this.oidcSecurityService.getAccessToken();
  }

  public getUserNameFromToken(): Observable<string> {
    // Return mock user name if set
    if (this.mockUserName) {
      return of(this.mockUserName);
    }
    return this.getAccessToken().pipe(
      filter(Boolean),
      map((token) => this.parseJWT(token).name),
    );
  }

  public getUserInfoFromToken(): Observable<UserInfo> {
    return this.getAccessToken().pipe(
      filter(Boolean),
      map((token) => this.parseJWT(token).email),
    );
  }

  public getRoles(): string[] {
    return this.userRoles;
  }

  public getUserEmail(): string {
    return this.userEmail;
  }

  private parseJWT(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  }
}

